import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Chip from '../../../components/Chip/Chip';
import Rate from '../../../components/Rate/Rate';
import Filter from '../../../components/Filter/Filter';
import Keyword from '../../../components/Keyword/Keyword';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import { Add } from '@mui/icons-material';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { useNavigate } from 'react-router-dom';
import KeywordMonitoringService from '../../../services/KeywordMonitoringService';
import { IKeywordMonitoring } from '../../../shared/interfaces/IKeywordMonitoring';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import { ReactComponent as KeywordsImage } from '../../../assets/images/keywords_no_content.svg';
import { AuthContext } from '../../../providers/AuthProvider';

const Keywords: FC = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rating, setRating] = useState(0);
  const [keywords, setKeywords] = useState<IKeywordMonitoring[]>([]);

  const [sort, setSort] = useState('');

  const onDelete = (id: string) => {
    setKeywords((prevState) => {
      return [...prevState.filter((item) => item.id !== id)];
    });
  };

  useEffect(() => {
    setCurrentView(t('nav_keywords'));
    document.title = `${t('app_branding')} ${t('nav_keywords')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const getKeywords = async (signal: AbortSignal) => {
      setLoading(true);
      const keywords: IKeywordMonitoring[] | ReputativError =
        await KeywordMonitoringService.getMonitoring(setLoading, signal);

      if (!Array.isArray(keywords)) return;
      if (keywords.length === 0) return;

      setKeywords(keywords);
      setLoading(false);
    };

    (async () => await getKeywords(controller.signal))();

    return () => {
      controller.abort();
      setLoading(false);
    };
  }, []);

  const searchTermInput = useRef<HTMLInputElement | null>(null);

  return (
    <Section>
      <>
        {keywords.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <KeywordsImage width="300" />
            <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
            {hasRole('can-add') && !exceedsLimit('limitkeywords', 1) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('guide')}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('add_monitoring')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <ContentContainer>
                <LeftContainer>
                  <ActionBar>
                    {hasRole('can-add') && !exceedsLimit('limitkeywords', keywords.length + 1) && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('guide')}
                        sx={{ ml: 'auto' }}
                      >
                        {t('add_monitoring')}
                      </Button>
                    )}
                  </ActionBar>
                  <SortContainer>
                    <Typography
                      variant="subtitle2"
                      sx={{ pl: theme.spacing(1) }}
                    >
                      {keywords.length} {keywords.length === 1 ? 'Keyword' : t('keywords')}
                    </Typography>
                    <FormControl
                      disabled
                      size="small"
                      sx={{ minWidth: '284px', display: 'none' }}
                    >
                      <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                      <Select
                        labelId="sort-select-label"
                        value={sort}
                        onChange={(event: SelectChangeEvent) => {
                          setSort(event.target.value);
                        }}
                        label={t('sort')}
                      >
                        <MenuItem value="">
                          <em>{t('unsorted')}</em>
                        </MenuItem>
                        <MenuItem value="1">{t('date_asc')}</MenuItem>
                        <MenuItem value="2">{t('date_desc')}</MenuItem>
                        <MenuItem value="3">{t('keyword_asc')}</MenuItem>
                        <MenuItem value="4">{t('keyword_desc')}</MenuItem>
                      </Select>
                    </FormControl>
                  </SortContainer>
                  {keywords.map((keyword) => {
                    return (
                      <Keyword
                        key={keyword.id}
                        monitoring_id={keyword.id}
                        platform={'google'}
                        search={keyword.search.search}
                        searchHash={keyword.searchHash}
                        country={keyword.search.country}
                        date={keyword.createdAt}
                        url={keyword.search.url}
                        onDelete={onDelete}
                      />
                    );
                  })}
                </LeftContainer>
                <RightContainer sx={{ display: 'none' }}>
                  <Filter
                    filters={
                      <>
                        <TextField
                          label={t('search')}
                          variant="outlined"
                          size="small"
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setSearchTerm(event.target.value);
                          }}
                          inputRef={searchTermInput}
                          sx={{ mb: theme.spacing(2), mt: theme.spacing(2) }}
                        />
                        <Divider />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            mt: theme.spacing(1),
                          }}
                        >
                          {t('rating')}
                        </Typography>
                        <Rate
                          rating={rating}
                          changeRating={setRating}
                        />
                      </>
                    }
                    activeFilters={
                      <>
                        {searchTerm !== '' && (
                          <Chip
                            label={searchTerm}
                            onDelete={() => {
                              searchTermInput.current!.value = '';
                              setSearchTerm('');
                            }}
                          />
                        )}
                      </>
                    }
                    resetActiveFilters={() => {
                      searchTermInput.current!.value = '';
                      setSearchTerm('');
                      setRating(0);
                    }}
                  />
                </RightContainer>
              </ContentContainer>
            )}
          </>
        )}
      </>
    </Section>
  );
};

export default Keywords;
